import React, { useMemo, useState } from "react"

import { httpOk, GET } from "helpers/api"
import { formatMoney, returnMinDate } from "helpers/helpers"
import PeriodPicker from "pages/parts/PeriodPicker"
import { ANALYTICS_DATE_RANGE_DAYS } from "../AnalyticsDatePicker"
import AnalyticsFilterBar, { FilterField, FilterSegment, FilterSelect, StandardFields } from "../parts/AnalyticsFilterBar"
import useAnalyticsFilters from "../parts/useAnalyticsFilters"
import useAnalyticsOptions from "../parts/useAnalyticsOptions"
import { buildAnalyticsUrl, localDateString, parseLocalDate } from "../parts/analyticsDates"

const cardStyle = {
	border: "1px solid var(--dark-border, #edf0f4)",
	borderRadius: "12px",
	boxShadow: "0 4px 14px rgba(15, 23, 42, 0.04)",
}

const mutedStyle = {
	color: "var(--dark-text-muted, #8791a1)",
	fontSize: "12px",
}

const monthNames = ["Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"]

const modeOptions = [
	{ id: "month", label: "Месяц", icon: "uil-calendar-alt" },
	{ id: "period", label: "Период" },
]

const compareOptions = [
	{ id: "prev", label: "Предыдущий месяц" },
	{ id: "year", label: "Год назад" },
]

function yesterdayDate() {
	const date = new Date()
	date.setHours(0, 0, 0, 0)
	date.setDate(date.getDate() - 1)
	return date
}

// Месяцы для выбора: последние 13, текущий — если в нём уже есть закрытый день
function monthOptionsList() {
	const last = yesterdayDate()
	return Array.from({ length: 13 }, (_, index) => {
		const month = new Date(last.getFullYear(), last.getMonth() - index, 1)
		return { id: localDateString(month), name: `${monthNames[month.getMonth()]} ${month.getFullYear()}` }
	})
}

function formatDateLabel(dateString) {
	if (!dateString) return ""
	const date = parseLocalDate(dateString)
	return `${String(date.getDate()).padStart(2, "0")}.${String(date.getMonth() + 1).padStart(2, "0")}.${date.getFullYear()}`
}

function formatPeriodLabel(startDateString, endDateString) {
	if (!startDateString || !endDateString) return ""
	return `${formatDateLabel(startDateString)} – ${formatDateLabel(endDateString)}`
}

function formatPercent(value) {
	return `${Number(value || 0).toFixed(1).replace(".0", "")}%`
}

function formatGrowth(value, unit) {
	if (value === null || value === undefined) return unit === "pp" ? "—" : "новое"
	const sign = Number(value) >= 0 ? "+" : ""
	const number = Number(value).toFixed(1).replace(".0", "")
	return unit === "pp" ? `${sign}${number} п.п.` : `${sign}${number}%`
}

function formatMetric(value) {
	const number = Number(value || 0)
	return formatMoney(number, 0)
}

function metricActual(metric) {
	return metric && typeof metric === "object" ? metric.actual : metric
}

function shortMetric(shortValue, value) {
	// Полное число из value; готовая строка с бэка — только если числа нет
	if (value !== null && value !== undefined) return formatMetric(value)
	return shortValue || formatMetric(0)
}

function planColor(percent) {
	if (percent === null || percent === undefined) return "#c4c6ca"
	const value = Number(percent || 0)
	if (value >= 100) return "#20bf87"
	if (value >= 80) return "#f0ad35"
	return "#e35d5d"
}

function foodCostStyle(value) {
	const percent = Number(value || 0)
	if (percent > 50) return { color: "#d84b5b", background: "var(--dark-tint-danger, #fdecec)" }
	if (percent > 40) return { color: "var(--dark-fg-warning, #b7791f)", background: "var(--dark-tint-warning, #fff4d8)" }
	if (percent < 30) return { color: "var(--dark-fg-success, #16875d)", background: "var(--dark-tint-success, #e7f8ef)" }
	return { color: "var(--dark-text, #4b5563)", background: "transparent" }
}

function GrowthValue({ value, unit }) {
	if (value === null || value === undefined) return <span className="text-muted">{formatGrowth(value, unit)}</span>
	const positive = Number(value) >= 0
	return (
		<span className={positive ? "text-success" : "text-danger"} style={{ whiteSpace: "nowrap" }}>
			{positive ? "↗" : "↘"} {formatGrowth(value, unit)}
		</span>
	)
}

function KpiCard({ title, value, metric, icon, compareLabel, progress = null }) {
	const growth = metric?.growth_percent
	const unit = metric?.growth_unit
	const isNew = growth === null || growth === undefined
	return (
		<div className="card mb-3 h-100" style={cardStyle}>
			<div className="card-body py-3 px-4">
				<div className="d-flex align-items-start gap-2 h-100">
					<i className={`uil ${icon} text-muted`} style={{ fontSize: "16px", lineHeight: "20px" }}></i>
					<div className="d-flex flex-column justify-content-between w-100">
						<div style={mutedStyle}>{title}</div>
						<h4 className="mb-1">{value}</h4>
						{metric !== undefined && (
							<div className="d-flex align-items-center gap-2" style={{ fontSize: "12px" }}>
								<span
									className={isNew ? "text-muted" : Number(growth) >= 0 ? "text-success" : "text-danger"}
									style={{ background: isNew ? "var(--dark-surface-2, #f1f4f8)" : Number(growth) >= 0 ? "var(--dark-tint-success, #e7f8ef)" : "var(--dark-tint-danger, #fdecec)", borderRadius: "14px", padding: "2px 8px", whiteSpace: "nowrap" }}
								>
									{formatGrowth(growth, unit)}
								</span>
								{compareLabel && <span className="text-muted">{compareLabel}</span>}
							</div>
						)}
						{progress !== null && (
							<>
								{compareLabel && metric === undefined && <div className="text-muted" style={{ fontSize: "12px" }}>{compareLabel}</div>}
								<div className="progress mt-2" style={{ height: "7px", borderRadius: "20px" }}>
									<div className="progress-bar" style={{ width: `${Math.min(Number(progress) || 0, 100)}%`, background: planColor(progress) }}></div>
								</div>
							</>
						)}
					</div>
				</div>
			</div>
		</div>
	)
}

function SectionCard({ title, subtitle, children, actions = null, titleIcon = null, style = {}, contentStyle = {} }) {
	return (
		<div className="card mb-3" style={{ ...cardStyle, ...style }}>
			<div className="card-body">
				<div className="d-flex justify-content-between align-items-start gap-3 mb-3">
					<div>
						<h5 className="mb-1 d-flex align-items-center gap-2">
							{titleIcon}
							{title}
						</h5>
						{subtitle && <div style={mutedStyle}>{subtitle}</div>}
					</div>
					{actions}
				</div>
				<div style={contentStyle}>
					{children}
				</div>
			</div>
		</div>
	)
}

function BranchDashboard({ items = [] }) {
	const maxRevenue = Math.max(...items.map(item => Number(item.revenue || 0)), 1)

	return (
		<div style={{ minWidth: "760px" }}>
			<div className="d-flex align-items-center gap-3 mb-3 text-muted text-uppercase" style={{ fontSize: "11px", letterSpacing: "0.4px" }}>
				<div style={{ width: "24px" }}></div>
				<div style={{ width: "180px" }}></div>
				<div className="flex-grow-1"></div>
				<div className="text-end" style={{ width: "64px" }}>Зал</div>
				<div className="text-end" style={{ width: "64px" }}>С собой</div>
				<div className="text-end" style={{ width: "64px" }}>Доставка</div>
				<div className="text-end" style={{ width: "64px" }}>План</div>
				<div className="text-end" style={{ width: "76px" }}>Выручка</div>
			</div>
			{items.map((item, index) => {
				const width = Math.max(Number(item.revenue || 0) / maxRevenue * 100, 6)
				const hasPlan = item.plan_execution_percent !== null && item.plan_execution_percent !== undefined
				return (
					<div className="d-flex align-items-center gap-3 mb-3" key={item.pos_id}>
						<div className="text-muted text-end" style={{ width: "24px", fontSize: "13px" }}>{index + 1}</div>
						<div className="text-truncate" style={{ width: "180px" }}>{item.pos_name}</div>
						<div className="flex-grow-1 position-relative" style={{ height: "20px", background: "var(--dark-surface-2, #f6f7f9)", borderRadius: "10px" }}>
							<div style={{ width: `${width}%`, height: "20px", borderRadius: "10px", background: planColor(item.plan_execution_percent) }}></div>
						</div>
						{["hall", "takeaway", "delivery"].map(code => (
							<div className="text-end fw-semibold text-muted" style={{ width: "64px", fontSize: "13px" }} key={code} title="Доля в выручке филиала">
								{formatPercent(item[code]?.share_percent)}
							</div>
						))}
						<div className="text-end fw-semibold" style={{ width: "64px", color: planColor(item.plan_execution_percent), fontSize: "13px" }}>
							{hasPlan ? formatPercent(item.plan_execution_percent) : "—"}
						</div>
						<div className="text-end fw-semibold" style={{ width: "70px" }}>{shortMetric(item.revenue_short, item.revenue)}</div>
					</div>
				)
			})}
			{items.length === 0 && <div className="text-muted">Нет данных</div>}
		</div>
	)
}

function OrderTypesBox({ orderTypes }) {
	const items = orderTypes?.data || []
	const totalCheques = Number(orderTypes?.total_cheques || 0)
	const colors = ["#4aa3df", "#36c690", "#f0b23a", "#d84b5b", "#8f5bd8", "#2f80c9"]

	return (
		<div>
			<div className="d-flex justify-content-between align-items-center mb-3">
				<span className="text-muted text-uppercase small">Всего чеков</span>
				<h3 className="mb-0">{formatMoney(totalCheques)}</h3>
			</div>
			{items.map((item, index) => {
				const color = colors[index % colors.length]
				const percent = item.cheques_share_percent || 0
				return (
					<div className="mb-3" key={item.sub_id}>
						<div className="d-flex justify-content-between align-items-center mb-1">
							<div className="d-flex align-items-center gap-2">
								<span style={{ width: "10px", height: "10px", borderRadius: "50%", background: color }}></span>
								<b>{item.name}</b>
							</div>
							<div className="text-end">
								<b>{formatMoney(item.cheques || 0)}</b>
								<span className="text-muted ms-2">{formatPercent(percent)}</span>
							</div>
						</div>
						<div className="progress" style={{ height: "7px", borderRadius: "20px" }}>
							<div className="progress-bar" style={{ width: `${Math.min(Number(percent || 0), 100)}%`, background: color }}></div>
						</div>
					</div>
				)
			})}
			{items.length === 0 && <div className="text-muted">Нет данных</div>}
		</div>
	)
}

function TopPositionsTable({ rows = [] }) {
	return (
		<div className="table-responsive">
			<table className="table align-middle mb-0" style={{ fontSize: "15px", borderCollapse: "separate", borderSpacing: 0 }}>
				<thead>
					<tr style={{ border: "none" }}>
						<th className="py-3 text-muted fw-semibold" style={{ border: "none", fontSize: "13px" }}>#</th>
						<th className="py-3 text-muted fw-semibold" style={{ border: "none", fontSize: "13px" }}>Позиция</th>
						<th className="py-3 text-muted fw-semibold text-end" style={{ border: "none", fontSize: "13px" }}>Шт.</th>
						<th className="py-3 text-muted fw-semibold text-end" style={{ border: "none", fontSize: "13px" }}>FC</th>
						<th className="py-3 text-muted fw-semibold text-end" style={{ border: "none", fontSize: "13px" }}>Доля</th>
						<th className="py-3 text-muted fw-semibold text-end" style={{ border: "none", fontSize: "13px" }}>Выручка</th>
						<th className="py-3 text-muted fw-semibold text-end" style={{ border: "none", fontSize: "13px" }}>Δ</th>
					</tr>
				</thead>
				<tbody>
					{rows.map(item => (
						<tr key={item.product_id}>
							<td className="py-3" style={{ borderBottom: "1px solid var(--dark-border, #eef2f6)" }}>{item.rank}</td>
							<td className="py-3" style={{ borderBottom: "1px solid var(--dark-border, #eef2f6)" }}><b>{item.product_name}</b></td>
							<td className="py-3 text-end" style={{ borderBottom: "1px solid var(--dark-border, #eef2f6)" }}>{formatMoney(item.quantity)}</td>
							<td className="py-3 text-end" style={{ borderBottom: "1px solid var(--dark-border, #eef2f6)" }}>
								<span className="fw-semibold" style={{ ...foodCostStyle(item.food_cost_percent), borderRadius: "14px", padding: "3px 8px", display: "inline-block", minWidth: "54px" }}>
									{formatPercent(item.food_cost_percent)}
								</span>
							</td>
							<td className="py-3 text-end" style={{ borderBottom: "1px solid var(--dark-border, #eef2f6)" }}>{formatPercent(item.share_percent)}</td>
							<td className="py-3 text-end" style={{ borderBottom: "1px solid var(--dark-border, #eef2f6)" }}>{shortMetric(item.revenue_short, item.revenue)}</td>
							<td className="py-3 text-end" style={{ borderBottom: "1px solid var(--dark-border, #eef2f6)" }}><GrowthValue value={item.revenue_growth_percent} /></td>
						</tr>
					))}
					{rows.length === 0 && (
						<tr><td colSpan="7" className="text-muted text-center py-4">Нет данных</td></tr>
					)}
				</tbody>
			</table>
		</div>
	)
}

function planSourceLabel(source) {
	if (source === "manual") return "ручной план"
	if (source === "auto") return "авто-план"
	if (source === "mixed") return "ручной + авто-план"
	return "план не задан"
}

function Monthly() {
	const [data, setData] = useState(null)
	const monthOptions = useMemo(() => monthOptionsList(), [])

	const filters = useAnalyticsFilters({
		pos_id: -1,
		order_type_ids: [],
		source_ids: [],
		mode: "month",
		month_start: monthOptions[0].id,
		start_date: monthOptions[0].id,
		end_date: localDateString(yesterdayDate()),
		compare: "prev",
	}, searchWithFilter)
	const { draft, patch } = filters
	const options = useAnalyticsOptions(["sources"], draft.pos_id)

	async function searchWithFilter(filter) {
		const params = {
			pos_id: filter.pos_id,
			order_type_ids: filter.order_type_ids,
			source_ids: filter.source_ids,
			compare: filter.compare,
			...(filter.mode === "period"
				? { start_date: filter.start_date, end_date: filter.end_date }
				: { month_start: filter.month_start }),
		}
		const response = await GET(buildAnalyticsUrl("/analytics/monthly", params), {}, { loader: true })
		if (httpOk(response)) setData(response.data)
	}

	const summary = data?.summary || {}
	const plan = data?.plan || {}
	const branches = data?.branches?.data || []
	const currentPeriodLabel = formatPeriodLabel(data?.start_date, data?.end_date)
	const comparePeriodLabel = formatPeriodLabel(data?.compare_start, data?.compare_end)
	const compareLabel = data?.compare_label
	const subtitle = data
		? `${data.mode === "month" ? data.month_label : ""}${data.mode === "month" ? " · " : ""}${currentPeriodLabel}${data.is_partial ? " (месяц не закончился)" : ""} · сравнение: ${comparePeriodLabel}`
		: ""
	const invalidRange = draft.mode === "period" && draft.start_date > draft.end_date

	return (
		<div className="pb-3">
			<AnalyticsFilterBar
				title="Monthly"
				icon="uil-calendar-alt"
				subtitle={subtitle}
				filters={filters}
				presets={false}
			>
				<StandardFields filters={filters} options={options} />
				<FilterField label="Период" wide>
					<div className="d-flex align-items-center gap-2 flex-wrap">
						<FilterSegment options={modeOptions} value={draft.mode} onChange={(id) => patch({ mode: id, compare: id === "month" || draft.compare === "year" ? draft.compare : "prev" })} />
						{draft.mode === "month" ? (
							<div className="flex-grow-1" style={{ minWidth: "170px" }}>
								<FilterSelect options={monthOptions} value={draft.month_start} onChange={(id) => id !== -1 && patch({ month_start: id })} />
							</div>
						) : (
							<div className="flex-grow-1" style={{ minWidth: "260px" }}>
								<PeriodPicker
									startDate={draft.start_date}
									endDate={draft.end_date}
									onChange={(period) => patch(period)}
									withTime={false}
									minDate={returnMinDate(new Date(), ANALYTICS_DATE_RANGE_DAYS)}
									maxDate={new Date()}
								/>
							</div>
						)}
					</div>
					{invalidRange && <div className="text-danger small mt-1">Дата начала позже даты окончания</div>}
				</FilterField>
				<FilterField label="Сравнить с" wide>
					<FilterSegment
						options={draft.mode === "month" ? compareOptions : [{ id: "prev", label: "Предыдущий период" }, { id: "year", label: "Год назад" }]}
						value={draft.compare}
						onChange={(id) => patch({ compare: id })}
					/>
				</FilterField>
			</AnalyticsFilterBar>

			<div className="row align-items-stretch mb-3">
				<div className="col-xl col-md-6 mb-3">
					<KpiCard title="Выручка" value={formatMetric(metricActual(summary.revenue))} metric={summary.revenue} compareLabel={compareLabel} icon="uil-money-bill" />
				</div>
				<div className="col-xl col-md-6 mb-3">
					<KpiCard
						title="Выполнение плана"
						value={plan.value > 0 ? formatPercent(summary.plan_execution_percent) : "—"}
						compareLabel={plan.value > 0 ? `план ${shortMetric(plan.value_short, plan.value)} · ${planSourceLabel(plan.source)}` : planSourceLabel(plan.source)}
						icon="uil-focus-target"
						progress={plan.value > 0 ? summary.plan_execution_percent : null}
					/>
				</div>
				<div className="col-xl col-md-6 mb-3">
					<KpiCard title="Чеки" value={formatMoney(metricActual(summary.cheques))} metric={summary.cheques} compareLabel={compareLabel} icon="uil-receipt" />
				</div>
				<div className="col-xl col-md-6 mb-3">
					<KpiCard title="Маржа" value={formatPercent(metricActual(summary.margin_percent))} metric={summary.margin_percent} compareLabel={compareLabel} icon="uil-percentage" />
				</div>
				<div className="col-xl col-md-6 mb-3">
					<KpiCard title="Средний чек" value={formatMetric(metricActual(summary.average_cheque))} metric={summary.average_cheque} compareLabel={compareLabel} icon="uil-chart-line" />
				</div>
				<div className="col-xl col-md-6 mb-3">
					<KpiCard title="Глубина чека" value={Number(metricActual(summary.cheque_depth) || 0).toFixed(1)} metric={summary.cheque_depth} compareLabel={compareLabel} icon="uil-layers" />
				</div>
			</div>

			<div className="row">
				<div className="col-xl-8">
					<SectionCard
						title="Дашборд"
						subtitle="Выручка по филиалам, доли каналов и выполнение плана"
						titleIcon={<i className="uil uil-apps"></i>}
						style={{ minHeight: "500px" }}
						contentStyle={{ maxHeight: "430px", overflow: "auto", paddingRight: "4px" }}
					>
						<BranchDashboard items={branches} />
					</SectionCard>
				</div>
				<div className="col-xl-4">
					<SectionCard
						title="Типы заказов"
						titleIcon={<i className="uil uil-receipt"></i>}
						style={{ minHeight: "500px" }}
						contentStyle={{ maxHeight: "430px", overflowY: "auto", paddingRight: "4px" }}
					>
						<OrderTypesBox orderTypes={data?.order_types} />
					</SectionCard>
				</div>
			</div>

			<SectionCard
				title="Топ-10 позиций меню"
				subtitle={`Доля и FC — от товарной выручки после скидки чека · рост ${compareLabel || ""}`}
				titleIcon={<i className="uil uil-restaurant"></i>}
			>
				<TopPositionsTable rows={data?.top_positions || []} />
			</SectionCard>
		</div>
	)
}

export default Monthly
